/**
 * @fileoverview ScrollToTop Component - Automatic scroll management on route changes
 * 
 * Provides automatic scroll management for navigation:
 * - Scroll to top on route changes
 * - Hash-based anchor navigation support
 * - Smooth scrolling with fallback
 * - Delayed execution for DOM readiness
 * - Accessibility improvements
 * - Route change detection
 * 
 * @component
 * @example
 * <ScrollToTop />
 * 
 * @requires React
 * @requires react-router-dom
 * 
 * @author By John Bellew - Infinia.ie - last updated 10/07/2025
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * ScrollToTop Component
 * 
 * Automatically manages scroll position on route changes and supports anchor navigation.
 * 
 * Features:
 * - Scroll to top on route changes
 * - Hash-based anchor navigation
 * - Smooth scrolling with fallbacks
 * - Delayed execution for DOM readiness
 * - Error handling for scroll operations
 * 
 * @component
 * @returns {null} This component doesn't render anything
 */
const ScrollToTop = () => {
    const { pathname, hash } = useLocation();

    useEffect(() => {
        const scrollToElement = () => {
            if (hash) {
                // Remove the '#' from the hash
                const id = hash.replace('#', '');
                const element = document.getElementById(id);
                
                if (element) {
                    // Try scrolling with smooth behavior first
                    try {
                        element.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start'
                        });
                    } catch (error) {
                        // Fallback to regular scroll if smooth scroll fails
                        element.scrollIntoView();
                    }
                }
            } else {
                window.scrollTo(0, 0);
            }
        };

        // Try immediately
        scrollToElement();
        
        // And also after a short delay to ensure DOM is ready
        const timeoutId = setTimeout(scrollToElement, 500);

        return () => clearTimeout(timeoutId);
    }, [pathname, hash]);

    return null;
};

export default ScrollToTop;
